/**
 * Estado da camada de chuva por satélite (Zustand). Registra o codec de URL
 * `j` (de JAXA: o `c` da chuva já é do radar) no import — `ui/modules.tsx`
 * importa este arquivo antes de `readUrlState()`.
 */
import { create } from 'zustand';
import { registerUrlCodec } from '../state/urlCodec';
import type { IndiceGsmap } from './api';

/** A camada começa desligada, como as outras camadas extras. */
export const DEFAULT_VISIVEL = false;
/** Como o radar: a nuvem do satélite aparece por baixo da chuva. */
export const DEFAULT_OPACIDADE = 0.85;
export const LIMITES_OPACIDADE = { min: 0.2, max: 1, step: 0.05 } as const;

export type EstadoCarga = 'idle' | 'loading' | 'ready' | 'error';

export interface Situacao {
  estado: EstadoCarga;
  erro: string | null;
}

/**
 * A chuva no quadro exibido:
 * `ok` na tela · `carregando` baixando (fica a anterior) · `sem` nenhuma hora
 * do GSMaP a até 30 min deste horário · `falhou` o arquivo não veio.
 */
export type EstadoNoQuadro = 'ok' | 'carregando' | 'sem' | 'falhou';

export interface ChuvaNoQuadro {
  estado: EstadoNoQuadro;
  /** Carimbo da hora na tela (ou pedida). */
  c: string | null;
}

export interface Progresso {
  baixados: number;
  total: number;
  falhas: number;
}

export interface GsmapState {
  visivel: boolean;
  opacidade: number;
  situacao: Situacao;
  indice: IndiceGsmap | null;
  noQuadro: ChuvaNoQuadro;
  progresso: Progresso;

  setVisivel: (on: boolean) => void;
  setOpacidade: (o: number) => void;
}

export const useGsmapStore = create<GsmapState>()((set) => ({
  visivel: DEFAULT_VISIVEL,
  opacidade: DEFAULT_OPACIDADE,
  situacao: { estado: 'idle', erro: null },
  indice: null,
  noQuadro: { estado: 'sem', c: null },
  progresso: { baixados: 0, total: 0, falhas: 0 },

  setVisivel: (visivel) => set({ visivel }),
  setOpacidade: (opacidade) => set({ opacidade }),
}));

/** `j=1` liga a camada; `o0.6` = opacidade, só quando difere do padrão. Ex.: `j=1o0.6`. */
export function encodeGsmap(s: Pick<GsmapState, 'visivel' | 'opacidade'>): string | null {
  if (!s.visivel) return null;
  return s.opacidade !== DEFAULT_OPACIDADE ? `1o${s.opacidade}` : '1';
}

export function decodeGsmap(value: string): Partial<Pick<GsmapState, 'visivel' | 'opacidade'>> | null {
  if (!value || value[0] !== '1') return null;
  const out: Partial<Pick<GsmapState, 'visivel' | 'opacidade'>> = { visivel: true };
  const o = /o(\d*\.?\d+)/.exec(value);
  if (o) {
    const n = Number(o[1]);
    out.opacidade = Number.isFinite(n) ? Math.min(LIMITES_OPACIDADE.max, Math.max(LIMITES_OPACIDADE.min, n)) : DEFAULT_OPACIDADE;
  }
  return out;
}

registerUrlCodec({
  key: 'j',
  encode: () => encodeGsmap(useGsmapStore.getState()),
  decode: (value) => {
    const s = decodeGsmap(value);
    if (s) useGsmapStore.setState(s);
  },
  subscribe: (onChange) =>
    useGsmapStore.subscribe((a, b) => {
      if (a.visivel !== b.visivel || a.opacidade !== b.opacidade) onChange();
    }),
});
